import { supabaseGestionPension as supabase } from '../config/settings/supabase-client'
import { getLogger } from '../core/debuggers'
import {
  getImmPercentageFromConfig,
  getUtmQuantityFromConfig,
  METHOD,
  normalizePensionMethod,
} from '../config/settings/constants'
import { fetchPensionConfig, fetchImmHistory } from './onboarding'

const logger = getLogger('dashboard.js')

const HISTORY_MONTHS = 12

export async function getDashboardIndicators({ userId }) {
  logger.debug('Calling RPC for userId:', userId)
  const { data, error } = await supabase.rpc('get_dashboard_indicators_with_security', {
    p_user_id: userId
  })

  if (error) throw error
  return data?.[0] ?? null
}

/**
 * Parse month dates in UTC so "2026-07-01" stays July in Chile (UTC-3/-4).
 */
const parseMonthDate = (value) => {
  if (value instanceof Date) return value
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day || 1))
  }
  return new Date(value)
}

const startOfMonthUtc = (date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))

const addMonthsUtc = (date, count) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + count, 1))

const monthKey = (value) => {
  const date = parseMonthDate(value)
  if (Number.isNaN(date.getTime())) return null
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

const formatChartLabel = (value) => {
  if (!value) return ''
  const date = parseMonthDate(value)
  if (Number.isNaN(date.getTime())) return ''

  const month = new Intl.DateTimeFormat('es-CL', {
    month: 'short',
    timeZone: 'UTC',
  }).format(date)
  const year = String(date.getUTCFullYear()).slice(-2)
  return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${year}`
}

const immValueForMonth = (immHistory, monthDate) => {
  if (!immHistory?.length || !monthDate) return null
  const monthTime = startOfMonthUtc(monthDate).getTime()
  const applicable = immHistory.find((row) => {
    const from = parseMonthDate(row.effectiveFrom)
    if (Number.isNaN(from.getTime())) return false
    return startOfMonthUtc(from).getTime() <= monthTime
  })
  return applicable?.value != null ? Number(applicable.value) : null
}

/**
 * Build a fixed multi-month series ending at the latest pension month (or today).
 * Real calculations win over estimates for the same month.
 */
const buildLastYearSeries = (points, { estimatesByMonth } = {}) => {
  const byMonth = new Map()

  points.forEach((point) => {
    const key = monthKey(point.month)
    if (!key) return
    const existing = byMonth.get(key)
    if (!existing || parseMonthDate(point.month) >= parseMonthDate(existing.month)) {
      byMonth.set(key, { ...point, source: 'calculated' })
    }
  })

  const latestPointDate = points.reduce((latest, point) => {
    const date = parseMonthDate(point.month)
    if (Number.isNaN(date.getTime())) return latest
    return !latest || date > latest ? date : latest
  }, null)

  const end = startOfMonthUtc(latestPointDate || new Date())
  const start = addMonthsUtc(end, -(HISTORY_MONTHS - 1))
  const series = []

  for (let i = 0; i < HISTORY_MONTHS; i += 1) {
    const date = addMonthsUtc(start, i)
    const key = monthKey(date)
    const match = byMonth.get(key)
    const estimate = estimatesByMonth?.get(key)
    const chosen = match || estimate || null
    const amount = chosen != null ? Number(chosen.amount) : null
    const source = match
      ? 'calculated'
      : estimate
        ? 'estimated'
        : null

    series.push({
      id: chosen?.id || `empty-${key}`,
      amount: Number.isFinite(amount) ? amount : null,
      month: `${key}-01`,
      label: formatChartLabel(date),
      status: match?.status || null,
      source,
      hasData: Number.isFinite(amount),
      isEstimated: source === 'estimated',
      isCalculated: source === 'calculated',
    })
  }

  return series
}

const fetchUtmByMonth = async () => {
  const { data, error } = await supabase
    .from('utm_history')
    .select('id, value, target_month')
    .order('target_month', { ascending: true })

  if (error) throw error

  const map = new Map()
  ;(data || []).forEach((row) => {
    const key = monthKey(row.target_month)
    if (!key) return
    map.set(key, Number(row.value))
  })
  return map
}

/**
 * Pension amount history for the current user (last 12 months, chart-ready).
 * Fills gaps with estimates from the active pension config + UTM/IMM history.
 */
export async function getPensionHistory({ userId }) {
  const { data, error } = await supabase
    .from('pension_calculations')
    .select(`
      id,
      calculated_amount,
      calculation_date,
      status,
      user_id,
      utm_history (
        target_month
      )
    `)
    .eq('user_id', userId)
    .order('calculation_date', { ascending: true })

  if (error) {
    logger.error('getPensionHistory error:', error)
    throw error
  }

  logger.debug('getPensionHistory raw rows:', data?.length ?? 0)

  const points = (data || [])
    .filter((item) => item.calculated_amount != null)
    .map((item) => {
      const month = item.utm_history?.target_month || item.calculation_date
      return {
        id: item.id,
        amount: Number(item.calculated_amount),
        month,
        status: item.status,
      }
    })
    .filter((item) => item.month && Number.isFinite(item.amount))
    .sort((a, b) => parseMonthDate(a.month) - parseMonthDate(b.month))

  let estimatesByMonth = new Map()

  try {
    const config = await fetchPensionConfig(userId)
    const method = config ? normalizePensionMethod(config.method) : null

    const end = startOfMonthUtc(
      points.reduce((latest, point) => {
        const date = parseMonthDate(point.month)
        if (Number.isNaN(date.getTime())) return latest
        return !latest || date > latest ? date : latest
      }, null) || new Date()
    )
    const start = addMonthsUtc(end, -(HISTORY_MONTHS - 1))

    if (method === METHOD.UTM) {
      const quantity = Number(getUtmQuantityFromConfig(config))
      if (Number.isFinite(quantity) && quantity > 0) {
        const utmByMonth = await fetchUtmByMonth()
        for (let i = 0; i < HISTORY_MONTHS; i += 1) {
          const date = addMonthsUtc(start, i)
          const key = monthKey(date)
          const utmValue = utmByMonth.get(key)
          if (!Number.isFinite(utmValue)) continue
          estimatesByMonth.set(key, {
            id: `estimate-${key}`,
            amount: Math.round(utmValue * quantity),
            month: `${key}-01`,
            source: 'estimated',
          })
        }
      }
    } else if (method === METHOD.IMM) {
      const percentage = Number(getImmPercentageFromConfig(config))
      if (Number.isFinite(percentage) && percentage > 0) {
        const immHistory = await fetchImmHistory()
        for (let i = 0; i < HISTORY_MONTHS; i += 1) {
          const date = addMonthsUtc(start, i)
          const key = monthKey(date)
          const immValue = immValueForMonth(immHistory, date)
          if (!Number.isFinite(immValue)) continue
          estimatesByMonth.set(key, {
            id: `estimate-${key}`,
            amount: Math.round((immValue * percentage) / 100),
            month: `${key}-01`,
            source: 'estimated',
          })
        }
      }
    }
  } catch (estimateError) {
    logger.error('getPensionHistory estimate fill failed', estimateError)
    estimatesByMonth = new Map()
  }

  logger.debug('getPensionHistory chart points:', points.length, 'estimates:', estimatesByMonth.size)

  return buildLastYearSeries(points, { estimatesByMonth })
}
